import { useEffect, useMemo, useState } from 'react';

import { Button } from '../shared/Button';

import {
    type ActivationItem,
    type EventItem,
    useActivations,
    useEvents,
    useEventSchedule,
} from '../hooks/events';

import { toIsoString, toLocalDateTimeInput } from '../utils/dates';
import { handleError } from '../utils/utils';

import styles from './Events.module.scss';
import {
    describeVersionGap,
    formatVersionRange,
    hasVersionBounds,
    resolveEffectiveVersionBounds,
} from './versionRange';

type EditableScheduleItem = {
    clientId: string;
    id?: string;
    activationId: string | null;
    startTimeLocal: string;
    endTimeLocal: string;
    position: number;
};

export function ScheduleView({
    initialEventId = '',
}: {
    initialEventId?: string;
}) {
    const { data: events = [] } = useEvents();
    const activationsQuery = useActivations();
    const {
        data: activations = [],
        isSuccess: activationsLoaded,
        isError: activationsFailed,
    } = activationsQuery;
    const [selectedEventId, setSelectedEventId] = useState(initialEventId);

    useEffect(() => {
        if (initialEventId) {
            setSelectedEventId(initialEventId);
        }
    }, [initialEventId]);
    const [scheduleRows, setScheduleRows] = useState<EditableScheduleItem[]>(
        [],
    );

    const { data: scheduleData, saveSchedule } = useEventSchedule(
        selectedEventId || undefined,
    );

    const eventActivations = useMemo(
        () => activations.filter((item) => item.eventId === selectedEventId),
        [activations, selectedEventId],
    );

    const selectedEvent = events.find((event) => event.id === selectedEventId);

    // Every publication needs an activation, so a schedule that still has rows
    // cannot be saved for an event without any. Clearing (empty row list) stays
    // allowed so legacy rows can be removed.
    const missingActivations =
        activationsLoaded &&
        Boolean(selectedEventId) &&
        eventActivations.length === 0 &&
        scheduleRows.length > 0;

    const activationsById = useMemo(
        () => new Map(eventActivations.map((item) => [item.id, item])),
        [eventActivations],
    );

    // A row is a gap risk when, across every row sharing its exact start/end
    // window, some client version would match nothing and the player sees an
    // empty event. Maps each affected row to a human description of which
    // versions are left out.
    const rowsWithVersionGap = useMemo(() => {
        const gaps = new Map<string, string>();

        for (const row of scheduleRows) {
            if (!row.startTimeLocal || !row.endTimeLocal) continue;

            const sameWindowRows = scheduleRows.filter(
                (other) =>
                    other.startTimeLocal &&
                    other.endTimeLocal &&
                    other.startTimeLocal === row.startTimeLocal &&
                    other.endTimeLocal === row.endTimeLocal,
            );

            const covering = sameWindowRows.map((other) => {
                const activation = other.activationId
                    ? activationsById.get(other.activationId)
                    : undefined;
                // Falls back to the event's defaults exactly like the server does,
                // so a slot with no activation still inherits them rather than
                // reading as "open to everyone".
                return resolveEffectiveVersionBounds(
                    selectedEvent ?? {},
                    activation,
                );
            });

            const gapDescription = describeVersionGap(covering);
            if (gapDescription) {
                gaps.set(row.clientId, gapDescription);
            }
        }

        return gaps;
    }, [scheduleRows, activationsById, selectedEvent]);

    // Sync schedule data into editable rows when remote data changes
    useEffect(() => {
        if (scheduleData?.publications) {
            setScheduleRows(
                scheduleData.publications.map((item) => ({
                    clientId: crypto.randomUUID(),
                    id: item.id,
                    activationId: item.activationId ?? null,
                    startTimeLocal: toLocalDateTimeInput(item.startTime),
                    endTimeLocal: toLocalDateTimeInput(item.endTime),
                    position: item.position,
                })),
            );
        }
    }, [scheduleData, selectedEventId]);

    function handleEventChange(eventId: string) {
        setSelectedEventId(eventId);
        setScheduleRows([]);
    }

    function addScheduleRow() {
        setScheduleRows((prev) => [
            ...prev,
            {
                clientId: crypto.randomUUID(),
                activationId: '',
                startTimeLocal: '',
                endTimeLocal: '',
                position: prev.length,
            },
        ]);
    }

    function updateScheduleRow(
        index: number,
        patch: Partial<EditableScheduleItem>,
    ) {
        setScheduleRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        );
    }

    function removeScheduleRow(index: number) {
        setScheduleRows((prev) => prev.filter((_, i) => i !== index));
    }

    async function onSaveSchedule() {
        if (!selectedEventId) return;

        const invalidDateRow = scheduleRows.find(
            (row) => !row.startTimeLocal || !row.endTimeLocal,
        );
        if (invalidDateRow) {
            handleError(
                'Every schedule row must have both a start time and an end time',
                undefined,
                false,
            );
            return;
        }

        const publications = [];
        for (const [index, row] of scheduleRows.entries()) {
            if (!row.activationId) {
                const message = !activationsLoaded
                    ? activationsFailed
                        ? 'Activations could not be loaded. Please retry before saving.'
                        : 'Activations are still loading. Please wait before saving.'
                    : eventActivations.length === 0
                      ? 'This event has no activations. Create an activation key first — every schedule row needs one.'
                      : 'Every schedule row must select an activation.';
                handleError(message, undefined, false);
                return;
            }

            publications.push({
                id: row.id,
                activationId: row.activationId,
                startTime: toIsoString(row.startTimeLocal),
                endTime: toIsoString(row.endTimeLocal),
                position: Number.isFinite(row.position) ? row.position : index,
            });
        }

        try {
            await saveSchedule.mutateAsync({
                publications,
            });
        } catch (err) {
            handleError('Failed to save schedule', err);
        }
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Publication schedule</h2>
                <p>
                    Select an event, add rows, and set date ranges for each
                    activation.
                </p>
            </div>

            <div className={styles.actionsBar}>
                <select
                    className={styles.select}
                    value={selectedEventId}
                    onChange={(e) => handleEventChange(e.target.value)}
                >
                    <option value="">Select event</option>
                    {events.map((event) => (
                        <option key={event.id} value={event.id}>
                            {event.eventKey}
                        </option>
                    ))}
                </select>

                <Button
                    type="button"
                    color="secondary"
                    onClick={addScheduleRow}
                    disabled={!selectedEventId}
                >
                    Add row
                </Button>

                <Button
                    type="button"
                    color="primary"
                    onClick={() => void onSaveSchedule()}
                    disabled={!selectedEventId || missingActivations}
                    loading={saveSchedule.isPending}
                    title={
                        missingActivations
                            ? 'This event has no activation key. Create one under Activations first.'
                            : undefined
                    }
                >
                    Save schedule
                </Button>
            </div>

            {!selectedEventId && (
                <div className={styles.emptyState}>
                    Choose an event to start scheduling.
                </div>
            )}

            {selectedEventId && (
                <>
                    <div className={styles.caption}>
                        Event:{' '}
                        <strong>
                            {selectedEvent?.eventKey ?? selectedEventId}
                        </strong>
                    </div>
                    <div className={styles.caption}>
                        Every row must select an activation.
                    </div>
                    {activationsLoaded && eventActivations.length === 0 && (
                        <div className={styles.warningMessage}>
                            This event has no activation key. Create one under{' '}
                            <strong>Activations</strong> before scheduling —
                            publications without an activation cannot be saved.
                        </div>
                    )}
                    {activationsFailed && (
                        <div className={styles.errorMessage}>
                            Unable to load activations. Activation availability
                            cannot be verified.
                        </div>
                    )}
                    {rowsWithVersionGap.size > 0 && (
                        <div className={styles.warningMessage}>
                            Some windows leave clients uncovered:{' '}
                            {[...new Set(rowsWithVersionGap.values())].join(
                                '; ',
                            )}
                            . Those players will see nothing for this event
                            during that time. Add an activation without bounds,
                            or widen the ranges so they meet.
                        </div>
                    )}
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Position</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Activation</th>
                                <th>Client version</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scheduleRows.map((row, index) => (
                                <ScheduleRow
                                    key={row.clientId}
                                    row={row}
                                    index={index}
                                    event={selectedEvent}
                                    eventActivations={eventActivations}
                                    activationsLoaded={activationsLoaded}
                                    activationsFailed={activationsFailed}
                                    versionGap={rowsWithVersionGap.get(
                                        row.clientId,
                                    )}
                                    onUpdate={updateScheduleRow}
                                    onRemove={removeScheduleRow}
                                />
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}

function ScheduleRow({
    row,
    index,
    event,
    eventActivations,
    activationsLoaded,
    activationsFailed,
    versionGap,
    onUpdate,
    onRemove,
}: {
    row: EditableScheduleItem;
    index: number;
    event: EventItem | undefined;
    eventActivations: ActivationItem[];
    activationsLoaded: boolean;
    activationsFailed: boolean;
    versionGap: string | undefined;
    onUpdate: (index: number, patch: Partial<EditableScheduleItem>) => void;
    onRemove: (index: number) => void;
}) {
    const selectedActivation = row.activationId
        ? eventActivations.find((item) => item.id === row.activationId)
        : undefined;
    const effectiveBounds = resolveEffectiveVersionBounds(
        event ?? {},
        selectedActivation,
    );

    return (
        <tr>
            <td>
                <input
                    className={styles.numberInput}
                    type="number"
                    value={row.position}
                    onChange={(e) =>
                        onUpdate(index, { position: Number(e.target.value) })
                    }
                />
            </td>
            <td>
                <input
                    className={styles.input}
                    type="datetime-local"
                    value={row.startTimeLocal}
                    onChange={(e) =>
                        onUpdate(index, { startTimeLocal: e.target.value })
                    }
                />
            </td>
            <td>
                <input
                    className={styles.input}
                    type="datetime-local"
                    value={row.endTimeLocal}
                    onChange={(e) =>
                        onUpdate(index, { endTimeLocal: e.target.value })
                    }
                />
            </td>
            <td>
                <select
                    className={styles.select}
                    value={row.activationId ?? ''}
                    onChange={(e) =>
                        onUpdate(index, { activationId: e.target.value })
                    }
                >
                    {eventActivations.length === 0 ? (
                        <option value="">
                            {activationsFailed
                                ? 'Activations unavailable'
                                : activationsLoaded
                                  ? 'No activation — create one first'
                                  : 'Loading activations...'}
                        </option>
                    ) : (
                        <option value="" disabled>
                            Select activation
                        </option>
                    )}
                    {eventActivations.map((item) => {
                        const bounds = resolveEffectiveVersionBounds(
                            event ?? {},
                            item,
                        );
                        return (
                            <option key={item.id} value={item.id}>
                                {hasVersionBounds(bounds)
                                    ? `${item.activationKey} (${formatVersionRange(bounds.minimumVersion, bounds.maximumVersion)})`
                                    : item.activationKey}
                            </option>
                        );
                    })}
                </select>
            </td>
            <td>
                <span
                    className={
                        hasVersionBounds(effectiveBounds)
                            ? styles.versionBadgeBounded
                            : styles.versionBadge
                    }
                    title={
                        versionGap
                            ? `Clients ${versionGap} see nothing during this window`
                            : undefined
                    }
                >
                    {formatVersionRange(
                        effectiveBounds.minimumVersion,
                        effectiveBounds.maximumVersion,
                    )}
                    {versionGap && ' ⚠'}
                </span>
            </td>
            <td>
                <Button
                    type="button"
                    color="danger"
                    size="small"
                    onClick={() => onRemove(index)}
                >
                    Remove
                </Button>
            </td>
        </tr>
    );
}
